import { visionLabApi } from '@/app/apiClient';
import type {
  AssignTaskResponse,
  CreateTaskRequestInput,
  EligibleAssigneesResponse,
  ReassignTaskResponse,
  TaskExecution,
  TaskRequest,
  TaskRequestDetail,
  TaskRequestFilters,
  TaskRequestPage,
} from '@/features/task-assignment/types/taskAssignment';
import type { ApiCallOptions } from '@/shared/api/client';

export async function fetchTaskRequests(
  filters: TaskRequestFilters,
  cursor: string | null,
  limit: number,
  options: ApiCallOptions = {},
): Promise<TaskRequestPage> {
  return visionLabApi.get<TaskRequestPage>('/manual-task-admin/task-requests', {
    ...options,
    searchParams: {
      limit,
      projectId: filters.projectId,
      workTypeId: filters.workTypeId,
      status: filters.status,
      cursor,
    },
  });
}

export async function fetchTaskRequestDetail(
  requestId: string,
  options: ApiCallOptions = {},
): Promise<TaskRequestDetail> {
  return visionLabApi.get<TaskRequestDetail>(
    `/manual-task-admin/task-requests/${encodeURIComponent(requestId)}`,
    options,
  );
}

export async function fetchEligibleAssignees(
  requestId: string,
  query?: string,
  options: ApiCallOptions = {},
): Promise<EligibleAssigneesResponse> {
  return visionLabApi.get<EligibleAssigneesResponse>(
    `/manual-task-admin/task-requests/${encodeURIComponent(requestId)}/eligible-assignees`,
    { ...options, searchParams: { q: query } },
  );
}

export async function createTaskRequest(
  input: CreateTaskRequestInput,
  options: ApiCallOptions = {},
): Promise<TaskRequest> {
  return visionLabApi.post<TaskRequest>('/manual-task-admin/task-requests', {
    ...options,
    json: input,
  });
}

export async function assignTask(
  requestId: string,
  assigneeId: string,
  options: ApiCallOptions = {},
): Promise<AssignTaskResponse> {
  return visionLabApi.post<AssignTaskResponse>(
    `/manual-task-admin/task-requests/${encodeURIComponent(requestId)}/executions`,
    { ...options, json: { assigneeId } },
  );
}

export async function reassignTask(
  executionId: string,
  assigneeId: string,
  options: ApiCallOptions = {},
): Promise<ReassignTaskResponse> {
  return visionLabApi.post<ReassignTaskResponse>(
    `/manual-task-admin/task-executions/${encodeURIComponent(executionId)}/reassign`,
    { ...options, json: { assigneeId } },
  );
}

export async function cancelTaskExecution(
  executionId: string,
  reason: string,
  options: ApiCallOptions = {},
): Promise<TaskExecution> {
  return visionLabApi.post<TaskExecution>(
    `/manual-task-admin/task-executions/${encodeURIComponent(executionId)}/cancel`,
    { ...options, json: { reason } },
  );
}

export async function closeTaskRequest(
  requestId: string,
  options: ApiCallOptions = {},
): Promise<TaskRequest> {
  return visionLabApi.post<TaskRequest>(
    `/manual-task-admin/task-requests/${encodeURIComponent(requestId)}/close`,
    options,
  );
}

export async function cancelTaskRequest(
  requestId: string,
  reason: string,
  options: ApiCallOptions = {},
): Promise<TaskRequest> {
  return visionLabApi.post<TaskRequest>(
    `/manual-task-admin/task-requests/${encodeURIComponent(requestId)}/cancel`,
    { ...options, json: { reason } },
  );
}
